/**
 * A chosen place ⇄ the API's location trio.
 *
 * `PlaceFilter` and `PlaceField` speak `{id, tier, name, trail?}` in the app's
 * tier names — `site`, `line`, `asset`. Every attached-to-a-place resource on the
 * wire speaks `plant_id`/`line_id`/`machine_id`, exactly one set. These two are
 * the one translation between them, so no form re-derives which column a tier
 * is.
 */

/** Every tier a record can attach to — not the account. */
export const ATTACHABLE_TIERS = new Set(['site', 'line', 'asset'])

/** `{tier, id}` → `{plantId, lineId, machineId}`, all three keys present. Null for no place. */
export function trioOf(place) {
  if (!place) return null
  const trio = { plantId: null, lineId: null, machineId: null }
  if (place.tier === 'site') return { ...trio, plantId: place.id }
  if (place.tier === 'line') return { ...trio, lineId: place.id }
  if (place.tier === 'asset') return { ...trio, machineId: place.id }
  return null
}

/**
 * A record's own place, for seeding a picker on an edit.
 *
 * `hierarchy` is the `{plant, line, machine}` names map every such response
 * carries; the deepest name is the place's own. Without a trail the picker
 * reopens at the top, which is the honest fallback: the ids above the place are
 * not on the record.
 *
 * @param {{plantId?: string, lineId?: string, machineId?: string, hierarchy?: object}} row
 */
export function placeOf(row) {
  if (!row) return null
  const names = row.hierarchy ?? {}
  if (row.machineId) return { id: row.machineId, tier: 'asset', name: names.machine ?? row.machineId }
  if (row.lineId) return { id: row.lineId, tier: 'line', name: names.line ?? row.lineId }
  if (row.plantId) return { id: row.plantId, tier: 'site', name: names.plant ?? row.plantId }
  return null
}

/** A hierarchy node (`useHierarchyChildren` / `usePlaceNodes` row) → a place. */
export const placeFromNode = (node) => (node ? { id: node.id, tier: node.tier, name: node.name } : null)
